import * as React from 'react';
import { Box, TablePagination, Typography } from '@mui/material';
import { MOBILE_BOTTOM_NAV_CLEARANCE } from '../../theme/tokens';

/**
 * 🔴 R15 Runde 3, W-5 (2026-10-21): die Hoehe der Fusszeile steht EINMAL.
 *
 * Bis hierher trug `ScraperListSkeleton.jsx` dieselbe 44 als eigene Zahl — zwei
 * Stellen, die auseinanderlaufen, sobald eine davon angefasst wird (die
 * Skelett-Reservierung ist genau die Nachbildung dieser Leiste). Kein Tippziel:
 * die Leiste sitzt ueber jeder Tabelle und darf nicht springen, wenn der Zeiger
 * grob wird (E9, 2026-10-07).
 *
 * Export statt Token: der Wert gehoert zu DIESEM Baustein, nicht zum Theme — er
 * beschreibt seine Anatomie, nicht eine Skala. Ein Export ist additiv; kein
 * Aufrufer aendert sich dadurch.
 */
export const FOOTER_MIN_HEIGHT = 44;

/**
 * Minimal sticky footer:
 *  - shows the leftSlot (count info) ALWAYS when count > 0
 *  - shows the page-size selector hidden + paginator only when count > rowsPerPage
 *  - width locked to content via maxWidth
 *
 * Was previously hidden entirely when count <= rowsPerPage; that confused
 * users who expected to always see the row count and a sticky bottom
 * affordance. Now we always render the bar, the paginator only appears
 * when there's more than one page worth of data.
 *
 * 🔴 `stickyOnMobile` (UI-Durchgang 2 Teil 4, FF1) — die Vorgabe `true` ist das
 *    heutige Verhalten und bleibt es fuer alle Aufrufer. `false` nimmt die Leiste
 *    auf `xs` in den FLUSS (`position: static`, keine Unterkante): die schwebende
 *    Leiste ist 44 px hoch und volle Breite, sie deckt mobil die Zone
 *    `[96 px, 140 px]` ueber der Viewport-Unterkante zu — und dort liegt die
 *    Werkzeugleiste der Tabelle, nicht nur das Listenende.
 *    Gesetzt wird es NUR an gemessenen Aufrufern: der Baustein wird an vielen
 *    Stellen gemountet, und fuer die uebrigen liegt weder Foto noch Messung vor
 *    (Regel 20 §1 — wer nicht messen kann, entscheidet konservativ).
 *    Der Name ist Hausform: `ViewHeader` fuehrt dieselbe Achse („mobil anders als
 *    ab `sm`"), dort mit dem umgekehrten Vorgabewert.
 *
 * 🔴 `labelDisplayedRows` (G14a-3 Nachbesserung 3, R16-B2, 2026-11-01) — ADDITIV.
 *    Ohne den Prop reicht der Baustein nichts an `TablePagination` durch, und MUI
 *    druckt seine eigene Vorgabe „1–50 of 1260". Fuer einen Aufrufer, dessen
 *    `count` eine UNTERE SCHRANKE ist (der Audit-Strom kappt je Quelle bei 1000),
 *    ist das „of N" eine geratene Gesamtzahl. Er uebergibt dann eine eigene
 *    Funktion, die nur den BEREICH nennt. Die Vorgabe bleibt `undefined` —
 *    MUI setzt seine eigene, und kein anderer Aufrufer setzt den Prop; ihr
 *    Verhalten ist damit unveraendert.
 */
export default function TableFooterSimple({
  count,
  page,
  onPageChange,
  maxWidth = 1440,
  bottom = 0,
  rowsPerPage = 50,
  leftSlot = null,
  stickyOnMobile = true,
  labelDisplayedRows,
}) {
  if (!count) return null;
  const showPagination = count > rowsPerPage;

  return (
    <Box
      // 🔴 F5-1 (UI-Durchgang 2 Teil 5, 2026-10-09): benannte Messflaeche der
      //    Blätterleiste. Sie traegt keine Rolle (kein `role`), und ein
      //    Laufzeit-Scan nach „der erste Kasten mit `position: sticky`" traefe
      //    je Seite einen anderen — die Hausform verlangt benannte Flaechen.
      //    Gemessen wird `H.5` in `tests/design/ui2-kopf-mobil.spec.ts`.
      data-testid="table-footer"
      sx={{
        position: stickyOnMobile ? 'sticky' : { xs: 'static', sm: 'sticky' },
        // 🔴 Mobil sitzt die schwebende Unterleiste ueber `bottom: 0` und deckte
        //    diese Leiste zu („Products: 235 · 1–50 of 235" war nicht lesbar).
        //    Die Hoehe kommt aus `theme/tokens.js`, nicht aus einer zweiten
        //    Zahl hier. Ab `sm` gilt weiter der Prop.
        //    🔴 Mit `stickyOnMobile={false}` entfaellt die `xs`-Unterkante ganz
        //    (`auto`) — eine `static`-Leiste braucht keine, und sie stehen zu
        //    lassen hiesse, eine Zusage zu behalten, die nichts mehr traegt.
        bottom: stickyOnMobile
          ? { xs: MOBILE_BOTTOM_NAV_CLEARANCE, sm: bottom }
          : { xs: 'auto', sm: bottom },
        // High z-index — must paint above hovered rows (zIndex 3) and
        // any slide-in row affordances (zIndex 5).
        zIndex: 6,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        maxWidth,
        mx: 'auto',
        px: 1,
        // E9 (2026-10-07): die 44 ist die HOEHE DER FUSSZEILE, kein Tippziel —
        // die Leiste sitzt ueber jeder Tabelle und darf nicht springen, wenn
        // der Zeiger grob wird. Sie bleibt darum flach stehen.
        // 🔴 R15 Runde 3, W-5: die Zahl steht als `FOOTER_MIN_HEIGHT` oben in
        // dieser Datei und wird vom Skelett mitgelesen — nicht zweimal getippt.
        minHeight: FOOTER_MIN_HEIGHT,
      }}
    >
      {leftSlot && (
        <Typography
          variant="caption"
          component="div"
          sx={{ flex: 1, color: 'text.secondary', pl: 1 }}
        >
          {leftSlot}
        </Typography>
      )}
      {showPagination && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          labelDisplayedRows={labelDisplayedRows}
        />
      )}
    </Box>
  );
}
